import { UUID } from 'angular2-uuid';
import Dexie, { Table } from 'dexie';
import { catchError, from, map, Observable, throwError } from 'rxjs';
import { LoggerService } from '../telemetry/logger.service';
import { StorageBase } from 'src/app/storage/models/storage-base';
import { IRepository } from 'src/app/repository/repository.interface';
import { DuplicateKeyError } from 'src/app/repository/duplicate-key.error';
import { RecordNotFoundError } from 'src/app/repository/record-not-found.error';

/**
 * Abstract base class that provides CRUD operations for repositories.
 * 
 * @template T The type of entity being stored in the repository
 */
export abstract class BaseRepository<T extends StorageBase> implements IRepository<T> {

	/**
	 * @param table The Dexie table instance for the repository.
	 * @param logger The logging service.
	 */
	constructor(
		private readonly table: Table<T, UUID>,
		private readonly logger: LoggerService
	) {}

	/** @inheritdoc */
	public GetAll$(): Observable<T[]> {
		return from(this.table.toArray()).pipe(
			catchError((error: any) => {
				this.logger.TrackError(`An error occurred retrieving all data from table: ${this.table.name}.`, error);
				return throwError(() => error);
			})
		);
	}

	/** @inheritdoc */
	public GetById$(id: UUID): Observable<T | null> {
		return from(this.table.get(id)).pipe(
			map((item: T | undefined) => item ?? null),
			catchError((error: any) => {
				this.logger.TrackError(`An error occurred retrieving data by ID: ${id} from table: ${this.table.name}`, error);
				return throwError(() => error);
			})
		);
	}

	/** @inheritdoc */
	public GetByIndex$(searchCriteria: Record<string, UUID |string | number>): Observable<T[]> {
		return from(this.table.where(searchCriteria).toArray()).pipe(
			catchError((error: any) => {
				this.logger.TrackError(`An error occurred querying data by index: ${JSON.stringify(searchCriteria)} from table: ${this.table.name}`, error);
				return throwError(() => error);
			})
		);
	}

	/** @inheritdoc */
	public Insert$(item: T): Observable<UUID> {
		return from(this.table.add(item)).pipe(
			catchError((error: any) => {
				// A duplicate key is an expected outcome that callers may recover from (e.g., treating the
				// existing record as authoritative). Only log when the failure is a genuine storage error.
				if (error instanceof Dexie.ConstraintError) {
					return throwError(() => new DuplicateKeyError(`Item with ID ${item.Id} already exists in table: ${this.table.name}.`));
				}
				this.logger.TrackError(`An error occurred saving data to table: ${this.table.name}`, error);
				return throwError(() => error);
			})
		);
	}

	/** @inheritdoc */
	public Update$(item: T): Observable<void> {
		return from(this.table.update(item.Id, item)).pipe(
			map((numberOfRecordsUpdated: number) => {
				if (numberOfRecordsUpdated === 0) {
					throw new RecordNotFoundError(`Item with ID ${item.Id} not found for update in table: ${this.table.name}.`);
				}
				return void 0;
			}),
			catchError((error: any) => {
				// A missing row is an expected outcome that callers may recover from (e.g., via insert).
				// Only log when the failure represents a genuine storage error worth surfacing.
				if (!(error instanceof RecordNotFoundError)) {
					this.logger.TrackError(`An error occurred updating data to table: ${this.table.name}`, error);
				}
				return throwError(() => error);
			})
		);
	}

	/** @inheritdoc */
	public DeleteById$(id: UUID): Observable<void> {
		return from(this.table.delete(id)).pipe(
			catchError((error: any) => {
				this.logger.TrackError(`An error occurred deleting data with ID: ${id} from table ${this.table.name}.`, error);
				return throwError(() => error);
			})
		);
	}
}
